import {isEmpty} from 'lodash';
import React, {FC, ReactNode, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {CustomFieldAttribute} from '../../../../libs/shared/core/src/models/api/customFieldAttributeModel';
import {StyledTextLabel} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {boxedInputColorReaders} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {InlineLinkButton} from '../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {CustomAttributeModel, CustomFieldModel} from '../../../models/api/customFieldModel';
import {helpCenterUrl} from '../../../store/stateless/navigationActions';
import {LensCustomAttributePicker} from '../customAttributes/lensCustomAttributePicker';
import {LensCustomFieldSearchDropdown} from '../customAttributes/lensCustomFieldSearchDropdown';

interface LensFormMultiSelectCustomFieldsProps {
  customAttributes: ReadonlyArray<CustomFieldAttribute>;
  customFields: ReadonlyArray<CustomFieldModel>;
  onAddCustomField: (model: CustomFieldModel) => void;
  onUpdateCustomAttribute: (attribute: CustomFieldAttribute) => void;
  onReplaceCustomField: (currentField: CustomFieldModel, newField: CustomFieldModel) => void;
  onRemoveCustomField: (arg: number) => void;
  isDisabled: boolean;
  hasError?: boolean;
  errorMessage?: ReactNode;
}

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  position: relative;
  left: -12px;
`;

const StyledEmptyCustomFieldsDiv = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.small};
  color: ${boxedInputColorReaders.textLabel};
  line-height: 1.3;
`;

export const LensFormMultiSelectCustomFields: FC<LensFormMultiSelectCustomFieldsProps> = ({
  customAttributes,
  customFields,
  onAddCustomField,
  onUpdateCustomAttribute,
  onReplaceCustomField,
  onRemoveCustomField,
  isDisabled,
}) => {
  const [addedCustomFieldId, setAddedCustomFieldId] = useState<number | undefined>(undefined);

  const customAttributeModels: ReadonlyArray<CustomAttributeModel> = useMemo(
    () =>
      customAttributes
        .map(({id, value}) => {
          const customField = customFields.find((cf) => cf.id === id);

          return customField ? {customField, value} : undefined;
        })
        .filter(isDefined),
    [customAttributes, customFields],
  );

  // Exclude custom fields which were already added.
  const availableCustomFields = useMemo(
    () => customFields.filter((customField) => !customAttributes.some((attr) => attr.id === customField.id)),
    [customAttributes, customFields],
  );

  const onAddAndFocusCustomField = useCallback(
    (customField: CustomFieldModel) => {
      onAddCustomField(customField);
      // Remember the custom field that is being added so we can focus it immediately.
      setAddedCustomFieldId(customField.id);
    },
    [onAddCustomField],
  );

  const onLearnMoreClick = () => {
    openInBrowser(`${helpCenterUrl}/en/articles/2172`);
  };

  const hasMoreCustomFields = availableCustomFields.length > 0;

  return (
    <StyledWrapperDiv>
      <StyledTextLabel $size={VisualSizesEnum.MEDIUM} $isDisabled={isDisabled}>
        <Trans id="lens-form-multiselect-customfields-label">Filtered by these custom fields</Trans>
      </StyledTextLabel>
      {customAttributeModels.map((customAttribute) => (
        <LensCustomAttributePicker
          key={customAttribute.customField.id}
          customAttribute={customAttribute}
          availableCustomFields={availableCustomFields}
          onUpdate={(value) => onUpdateCustomAttribute({id: customAttribute.customField.id, value})}
          onReplace={(newField) => onReplaceCustomField(customAttribute.customField, newField)}
          onDelete={() => onRemoveCustomField(customAttribute.customField.id)}
          shouldStartInEditMode={addedCustomFieldId === customAttribute.customField.id}
        />
      ))}
      {hasMoreCustomFields && (
        <PopoverCoordinator
          isDisabled={isDisabled}
          renderButton={(isOpen) => (
            <StyledButtonGroup>
              <Button
                variant={ButtonVariantsEnum.LINK}
                hasHover={isOpen}
                isDisabled={isDisabled}
                renderTooltip={() =>
                  isDisabled && (
                    <Trans id="lens-form-multiselect-missing-inbox-message">
                      Field will be enabled once an inbox is selected.
                    </Trans>
                  )
                }
              >
                <Trans id="lens-form-multiselect-customfields-placeholder">Add custom field</Trans>
              </Button>
            </StyledButtonGroup>
          )}
          renderModal={(popoverProps) => (
            <LensCustomFieldSearchDropdown
              customFields={availableCustomFields}
              onSelect={onAddAndFocusCustomField}
              selectedIds={[]}
              {...popoverProps}
            />
          )}
        />
      )}
      {isEmpty(customFields) && (
        <StyledEmptyCustomFieldsDiv>
          <Trans id="lens-form-multiselect-customfields-empty">
            You do not have any Conversations custom fields
          </Trans>
          <br />
          <InlineLinkButton onClick={onLearnMoreClick}>
            <Trans id="lens-form-multiselect-customfields-learnmore">Learn more about custom fields</Trans>
          </InlineLinkButton>
        </StyledEmptyCustomFieldsDiv>
      )}
    </StyledWrapperDiv>
  );
};
